import { FC } from 'react';

import { OrderLineBOL, OrderLineDeliveryNote } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

interface Props {
   deliveryNotes: OrderLineDeliveryNote[];
   billsOfLading: OrderLineBOL[];
   onDownload: (document: OrderLineDeliveryNote) => void;
   onViewDocument: (document: OrderLineDeliveryNote) => void;
   onRequestDeliveryNote: (deliveryNote: OrderLineDeliveryNote) => void;
   onRequestBol: (bol: OrderLineBOL) => void;
}

export const DeliveryNoteDocuments: FC<Props> = ({
   deliveryNotes,
   billsOfLading,
   onDownload,
   onViewDocument,
   onRequestDeliveryNote,
   onRequestBol
}: Props) => {
   const translate = useTranslations();
   const format = useFormatter();

   if (deliveryNotes.length === 0 && billsOfLading.length === 0) {
      return (
         <Typography variant="body" component="span">
            {translate('page-consumer.order-line-flyout.no-documents-available')}
         </Typography>
      );
   }

   return (
      <>
         {deliveryNotes.map((deliveryNote, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <Box key={index} gap="none">
               <Box direction="column" gap="sm" width="100%">
                  <Box gap="xl">
                     <Box direction="column" gap="none">
                        <Typography variant="body" component="span" color="subtle">
                           {translate('page-consumer.order-line-flyout.transport-order-number')}
                        </Typography>
                        <Typography variant="body" component="span">
                           {deliveryNote.transportOrderNr}
                        </Typography>
                     </Box>
                     <Box direction="column" gap="none">
                        <Typography variant="body" component="span" color="subtle">
                           {translate('page-consumer.order-line-flyout.document-reference')}
                        </Typography>
                        <Typography variant="body" component="span">
                           {deliveryNote.deliveryNoteNr}
                        </Typography>
                     </Box>
                  </Box>
                  <Box justify="between">
                     <Box direction="column" gap="none">
                        <Typography variant="body" component="span" color="subtle">
                           {translate('page-consumer.order-line-flyout.document-date')}
                        </Typography>
                        <Typography variant="body" component="span">
                           {deliveryNote.documentDate ? format.dateTime(deliveryNote.documentDate) : '-'}
                        </Typography>
                     </Box>
                     <Box>
                        {deliveryNote.id ? (
                           <Box gap="2xs">
                              <IconButton icon="download" ariaLabel="Download delivery note" onClick={() => onDownload(deliveryNote)} />
                              <IconButton icon="eye" ariaLabel="View delivery note" onClick={() => onViewDocument(deliveryNote)} />
                           </Box>
                        ) : (
                           <Button variant="tertiary" onClick={() => onRequestDeliveryNote(deliveryNote)}>
                              {translate('page-consumer.order-line-flyout.request')}
                           </Button>
                        )}
                     </Box>
                  </Box>
                  {index < deliveryNotes.length - 1 && <Divider />}
               </Box>
            </Box>
         ))}
         {billsOfLading.map((billOfLading, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <Box key={index} gap="none">
               <Box direction="column" gap="sm" width="100%">
                  <Box gap="xl" justify="between">
                     <Box direction="column" gap="none">
                        <Typography variant="body" component="span" color="subtle">
                           {translate('page-consumer.order-line-flyout.transport-order-number')}
                        </Typography>
                        <Typography variant="body" component="span">
                           {billOfLading.transportOrderNr}
                        </Typography>
                     </Box>
                     <Box justify="end">
                        <Button variant="tertiary" onClick={() => onRequestBol(billOfLading)}>
                           {translate('page-consumer.order-line-flyout.request')}
                        </Button>
                     </Box>
                  </Box>
                  {index < billsOfLading.length - 1 && <Divider />}
               </Box>
            </Box>
         ))}
      </>
   );
};
